import { useState, useEffect } from "react";
import { authFetch } from "../api";

// Review-and-apply modal (verification refactor). Opens on "Auto Verify":
//   1. POST /auto-verify/preview  -> current vs RP/SE/AI diff + corrected history/DOM (no writes)
//   2. user picks a source per field (radio)
//   3. POST /auto-verify/apply    -> persists the picked fields + stamps a human verify
// (The apply endpoint still accepts ?dry_run=true for QA — it executes + rolls back — but
//  the UI always does a real save now.)

const LOADING_STAGES = [
  "Fetching RealPlus feed…",
  "Running AI on the description…",
  "Scraping StreetEasy…",
  "Comparing sources…",
];
const SOURCE_LABEL = { rp: "RealPlus", se: "StreetEasy", ai: "AI (description)" };

// What a manual override looks like per field. Every source can agree and still be wrong --
// a terrace none of them mention is still a terrace -- so each field needs a way for a person
// to say so directly, and "all three said No" must not be the end of the conversation.
// A typed value is sent with source "manual", which the apply step records as field_sources
// = human: the most reliable thing we hold, and the only one that outranks a later scrape.
const BOOL_OPTS = [{ v: true, t: "Yes" }, { v: false, t: "No" }];
const MANUAL = {
  dishwasher:            { kind: "bool" },
  washer_dryer:          { kind: "bool" },
  private_outdoor_space: { kind: "bool" },
  views:                 { kind: "bool" },
  outdoor_type:          { kind: "select", opts: ["terrace", "balcony", "garden", "roof_deck",
                                                  "patio", "juliet", "loggia", "none"] },
  renovation:            { kind: "select", opts: ["1", "2", "3", "4", "5"] },
  floor:                 { kind: "number", min: 0, step: 1 },
  bedrooms:              { kind: "number", min: 0, step: 1 },
  bathrooms:             { kind: "number", min: 0, step: 0.5 },
  sqft:                  { kind: "number", min: 0, step: 1 },
  list_price:            { kind: "number", min: 0, step: 1 },
  closing_price:         { kind: "number", min: 0, step: 1 },
  date_listed:           { kind: "date" },
};

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

export default function AutoVerifyModal({ listingId, onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [picks, setPicks] = useState({}); // field -> "current" | "rp" | "se" | "ai" | "manual"
  const [manuals, setManuals] = useState({}); // field -> value the user typed
  const [domPick, setDomPick] = useState("stored"); // "stored" | "calc" | "se"
  const [replaceHistory, setReplaceHistory] = useState(false);
  const [showSeRows, setShowSeRows] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(
      () => setStage((s) => Math.min(s + 1, LOADING_STAGES.length - 1)),
      1600
    );
    return () => clearInterval(t);
  }, [loading]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStage(0);
    authFetch(`/listings/${listingId}/auto-verify/preview`, { method: "POST" })
      .then((data) => {
        if (cancelled) return;
        if (data?.detail || data?.error) {
          setError(data.detail || data.error);
          setLoading(false);
          return;
        }
        setPreview(data);
        const p = {};
        (data.fields || []).forEach((f) => {
          p[f.field] = f.proposed ? f.proposed.source : "current";
        });
        setPicks(p);
        setDomPick(data.dom?.proposed ? data.dom.proposed.source : "stored");
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(String(e));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listingId]);

  const setPick = (field, src) => setPicks((p) => ({ ...p, [field]: src }));
  // Typing in an override selects it -- otherwise the value is entered and silently ignored
  // because the radio still points at whatever was there before.
  const setManual = (field, value) => {
    setManuals((m) => ({ ...m, [field]: value }));
    setPicks((p) => ({ ...p, [field]: "manual" }));
  };

  const accepted = {};
  const sources = {};
  (preview?.fields || []).forEach((f) => {
    const src = picks[f.field];
    if (src === "manual") {
      const v = manuals[f.field];
      if (v !== undefined && v !== "") {
        accepted[f.field] = v;
        sources[f.field] = "manual";
      }
      return;
    }
    if (src && src !== "current" && f.sources[src] !== undefined) {
      accepted[f.field] = f.sources[src];
      sources[f.field] = src;
    }
  });
  // DOM is its own picker (stored / calc / se-page) → active_dom
  const dom = preview?.dom;
  if (dom && domPick !== "stored") {
    const domVal = domPick === "se" ? dom.se_page : dom.calc;
    if (domVal !== null && domVal !== undefined && domVal !== dom.stored) {
      accepted.active_dom = domVal;
    }
  }
  const seRows = preview?.history?.se_rows || [];
  const canReplaceHistory = seRows.length > 0;
  const changeCount = Object.keys(accepted).length;
  const hasChanges = changeCount > 0 || (replaceHistory && canReplaceHistory);

  const apply = async () => {
    setApplying(true);
    setApplyMsg(null);
    try {
      const r = await authFetch(`/listings/${listingId}/auto-verify/apply`, {
        method: "POST",
        body: JSON.stringify({
          accepted,
          sources,   // which radio produced each value -> per-field provenance on the write
          replace_history: replaceHistory && canReplaceHistory ? seRows : null,
        }),
      });
      if (r?.success) {
        const n = Object.keys(r.applied || {}).length;
        const hp = r.history_replaced ? `, ${r.history_replaced} history rows` : "";
        setApplyMsg(
          n === 0 && !hp
            ? "Verified ✓"
            : `Verified — applied ${n} change${n === 1 ? "" : "s"}${hp} ✓`
        );
        onApplied?.(r);
        setTimeout(() => onClose?.(), 700);
      } else {
        setApplyMsg(`Failed — ${r?.detail || "unknown error"}`);
      }
    } catch (e) {
      setApplyMsg(`Failed — ${e}`);
    } finally {
      setApplying(false);
    }
  };

  const hist = preview?.history;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.panel} onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div style={S.header}>
          <div style={{ fontWeight: 600, fontSize: "15px" }}>
            Review &amp; Apply — Listing {listingId}
          </div>
          <button onClick={onClose} style={S.close}>
            &times;
          </button>
        </div>

        {/* body */}
        <div style={S.body}>
          {loading && (
            <div style={S.loadingWrap}>
              <div style={S.spinner} />
              <div style={{ marginTop: "14px", color: "#555", fontSize: "13px" }}>
                {LOADING_STAGES[stage]}
              </div>
              <div style={{ marginTop: "6px", color: "#999", fontSize: "11px" }}>
                Scraping StreetEasy can take up to ~2 minutes.
              </div>
            </div>
          )}

          {!loading && error && (
            <div style={S.error}>Could not build preview — {error}</div>
          )}

          {!loading && preview && (
            <>
              {/* sources */}
              <div style={S.sources}>
                {["rp", "se", "ai"].map((s) => {
                  const st = preview.sources_status?.[s] || "";
                  const ok = st.startsWith("ok");
                  return (
                    <span key={s} style={{ ...S.srcChip, color: ok ? "#2e7d32" : "#c62828" }}>
                      {ok ? "✓" : "✕"} {SOURCE_LABEL[s]}
                    </span>
                  );
                })}
              </div>

              {/* field table */}
              <div style={S.tableHead}>
                <div style={{ flex: "0 0 130px" }}>Field</div>
                <div style={{ flex: 1 }}>Pick the value to apply</div>
              </div>
              {preview.fields.map((f) => {
                const changed = picks[f.field] && picks[f.field] !== "current";
                const options = [
                  { key: "current", label: "Current", val: f.current },
                  ...["rp", "se", "ai"]
                    .filter((s) => f.sources[s] !== undefined)
                    .map((s) => ({ key: s, label: SOURCE_LABEL[s], val: f.sources[s] })),
                ];
                const noSources = options.length === 1; // only "current"
                const man = MANUAL[f.field];
                const manSel = picks[f.field] === "manual";
                return (
                  <div
                    key={f.field}
                    style={{ ...S.row, background: changed ? "#fff8e1" : "transparent" }}
                  >
                    <div style={S.rowLabel}>
                      {f.label}
                      {changed && <span style={S.changedDot} title="will change" />}
                    </div>
                    <div style={S.opts}>
                      {noSources && (
                        <label
                          style={{ ...S.opt, borderColor: !manSel ? "#1976d2" : "#ddd",
                                   background: !manSel ? "#e3f2fd" : "#fff" }}
                        >
                          <input
                            type="radio"
                            name={`f_${f.field}`}
                            checked={!manSel}
                            onChange={() => setPick(f.field, "current")}
                            style={{ marginRight: "5px" }}
                          />
                          <span style={{ color: "#888", fontSize: "10px", marginRight: "4px" }}>Current</span>
                          <b>{fmt(f.current)}</b>
                          <span style={{ ...S.tag, background: "#eee", color: "#777" }}>no other source</span>
                        </label>
                      )}
                      {!noSources &&
                        options.map((o) => {
                          const sel = picks[f.field] === o.key;
                          const isCurrent = o.key === "current";
                          return (
                            <label
                              key={o.key}
                              style={{
                                ...S.opt,
                                borderColor: sel ? "#1976d2" : "#ddd",
                                background: sel ? "#e3f2fd" : "#fff",
                              }}
                            >
                              <input
                                type="radio"
                                name={`f_${f.field}`}
                                checked={sel}
                                onChange={() => setPick(f.field, o.key)}
                                style={{ marginRight: "5px" }}
                              />
                              <span style={{ color: "#888", fontSize: "10px", marginRight: "4px" }}>
                                {o.label}
                              </span>
                              <b>{fmt(o.val)}</b>
                              {isCurrent && f.flags?.some((x) => x === "current_is_estimated") && (
                                <span style={S.tag}>est.</span>
                              )}
                            </label>
                          );
                        })}
                      {man && (
                        <label
                          style={{
                            ...S.opt,
                            borderColor: manSel ? "#7b1fa2" : "#ddd",
                            background: manSel ? "#f3e5f5" : "#fff",
                          }}
                        >
                          <input
                            type="radio"
                            name={`f_${f.field}`}
                            checked={manSel}
                            onChange={() => setPick(f.field, "manual")}
                            style={{ marginRight: "5px" }}
                          />
                          <span style={{ color: "#7b1fa2", fontSize: "10px", marginRight: "4px", fontWeight: 600 }}>
                            I know
                          </span>
                          {man.kind === "bool" && (
                            <select
                              value={manuals[f.field] === undefined ? "" : String(manuals[f.field])}
                              onChange={(e) => setManual(f.field, e.target.value === "true")}
                              style={S.manualInput}
                            >
                              <option value="" disabled>—</option>
                              {BOOL_OPTS.map((b) => (
                                <option key={String(b.v)} value={String(b.v)}>{b.t}</option>
                              ))}
                            </select>
                          )}
                          {man.kind === "select" && (
                            <select
                              value={manuals[f.field] ?? ""}
                              onChange={(e) => setManual(f.field, e.target.value)}
                              style={S.manualInput}
                            >
                              <option value="" disabled>—</option>
                              {man.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          )}
                          {man.kind === "number" && (
                            <input
                              type="number"
                              min={man.min}
                              step={man.step}
                              value={manuals[f.field] ?? ""}
                              onChange={(e) => setManual(f.field, e.target.value === "" ? "" : Number(e.target.value))}
                              placeholder="—"
                              style={{ ...S.manualInput, width: "80px" }}
                            />
                          )}
                          {man.kind === "date" && (
                            <input
                              type="date"
                              value={manuals[f.field] ?? ""}
                              onChange={(e) => setManual(f.field, e.target.value)}
                              style={{ ...S.manualInput, width: "130px" }}
                            />
                          )}
                        </label>
                      )}
                      {f.flags?.map((fl, i) =>
                        fl && fl.ai_reasoning ? (
                          <div key={i} style={S.reason} title={fl.ai_reasoning}>
                            ⓘ AI: {fl.ai_reasoning.slice(0, 90)}
                            {fl.ai_reasoning.length > 90 ? "…" : ""}
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Days on Market — pick stored / my-calc / SE-page (applies to active_dom) */}
              <div style={S.domBox}>
                <div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "8px" }}>
                  Days on Market
                </div>
                <div style={S.opts}>
                  {[
                    { key: "stored", label: "Current", val: dom?.stored },
                    { key: "calc", label: "My calc", val: dom?.calc },
                    { key: "se", label: "StreetEasy", val: dom?.se_page },
                  ]
                    .filter((o) => o.key === "stored" || (o.val !== null && o.val !== undefined))
                    .map((o) => {
                      const sel = domPick === o.key;
                      return (
                        <label
                          key={o.key}
                          style={{ ...S.opt, borderColor: sel ? "#1976d2" : "#ddd", background: sel ? "#e3f2fd" : "#fff" }}
                        >
                          <input type="radio" name="dom" checked={sel} onChange={() => setDomPick(o.key)} style={{ marginRight: "5px" }} />
                          <span style={{ color: "#888", fontSize: "10px", marginRight: "4px" }}>{o.label}</span>
                          <b>{o.val ?? "—"}{o.val != null ? " days" : ""}</b>
                        </label>
                      );
                    })}
                </div>
                {dom?.cycle && (
                  <div style={{ fontSize: "11px", color: "#777", marginTop: "6px", lineHeight: 1.6 }}>
                    SE current cycle: listed <b>{dom.cycle.listed_at}</b> → stop{" "}
                    <b>{dom.cycle.stop_at || "still on market"}</b>
                    {dom.cycle.stop_event ? ` (${dom.cycle.stop_event})` : ""}
                    {dom.cycle.periods?.map((p, i) => (
                      <span key={i} style={{ display: "block", marginLeft: "10px" }}>
                        active {p.from} → {p.to} ({p.days}d)
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #eceff1" }}>
                  <div style={{ fontSize: "11px", color: "#666", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span>
                      History rows — Ours: <b>{hist?.ours ?? "—"}</b> · StreetEasy: <b>{hist?.se ?? "—"}</b>
                      {hist && hist.se > hist.ours && (
                        <span style={{ color: "#e65100" }}> (we're missing {hist.se - hist.ours})</span>
                      )}
                    </span>
                    {canReplaceHistory && (
                      <button type="button" onClick={() => setShowSeRows((s) => !s)}
                        style={{ border: "none", background: "none", color: "#1976d2", cursor: "pointer", fontSize: "11px", padding: 0 }}>
                        {showSeRows ? "hide" : "review"} StreetEasy rows
                      </button>
                    )}
                  </div>

                  {showSeRows && (
                    <div style={{ maxHeight: "160px", overflowY: "auto", marginTop: "6px", border: "1px solid #eee", borderRadius: "6px" }}>
                      {seRows.map((r, i) => (
                        <div key={i} style={{ display: "flex", gap: "10px", fontSize: "11px", padding: "3px 8px", borderBottom: "1px solid #f5f5f5" }}>
                          <span style={{ width: "78px", color: "#555", fontVariantNumeric: "tabular-nums" }}>{r.changed_date}</span>
                          <span style={{ width: "70px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.list_price != null ? `$${r.list_price.toLocaleString()}` : "—"}</span>
                          <span style={{ width: "110px", color: r.listing_status ? "#333" : "#bbb" }}>{r.listing_status || "—"}</span>
                          <span style={{ color: "#777" }}>{r.event_name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {canReplaceHistory && (
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", fontSize: "12px", color: replaceHistory ? "#c62828" : "#555", cursor: "pointer" }}>
                      <input type="checkbox" checked={replaceHistory} onChange={(e) => setReplaceHistory(e.target.checked)} />
                      Replace our {hist?.ours ?? 0} rows with StreetEasy's {seRows.length} (StreetEasy is more reliable)
                    </label>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* footer */}
        {!loading && preview && (
          <div style={S.footer}>
            {applyMsg && (
              <span style={{ fontSize: "12px", color: applyMsg.startsWith("Failed") ? "#c62828" : "#2e7d32" }}>
                {applyMsg}
              </span>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={onClose} style={S.cancelBtn}>
              Cancel
            </button>
            <button
              onClick={apply}
              disabled={applying}
              style={{
                ...S.applyBtn,
                opacity: applying ? 0.5 : 1,
                cursor: applying ? "default" : "pointer",
                background: "#1976d2",
              }}
            >
              {applying
                ? "Saving…"
                : hasChanges
                ? `Apply ${changeCount} change${changeCount === 1 ? "" : "s"}${replaceHistory && canReplaceHistory ? " + history" : ""} & Verify`
                : "Verify — no changes"}
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes avspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 },
  panel: { background: "#fff", borderRadius: "10px", width: "760px", maxWidth: "94vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 48px rgba(0,0,0,0.3)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #eee" },
  close: { border: "none", background: "none", fontSize: "24px", lineHeight: 1, cursor: "pointer", color: "#888" },
  body: { padding: "14px 18px", overflowY: "auto", flex: 1 },
  loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 0" },
  spinner: { width: "34px", height: "34px", border: "3px solid #e0e0e0", borderTopColor: "#1976d2", borderRadius: "50%", animation: "avspin 0.9s linear infinite" },
  error: { padding: "18px", background: "#fdecea", color: "#c62828", borderRadius: "6px", fontSize: "13px" },
  sources: { display: "flex", gap: "12px", marginBottom: "12px" },
  manualInput: {
    border: "1px solid #ce93d8", borderRadius: "3px", padding: "1px 4px",
    fontSize: "12px", fontWeight: 700, color: "#4a148c", background: "#fff",
  },
  srcChip: { fontSize: "11px", fontWeight: 600 },
  tableHead: { display: "flex", gap: "10px", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#999", padding: "0 0 6px", borderBottom: "1px solid #eee" },
  row: { display: "flex", gap: "10px", alignItems: "flex-start", padding: "7px 4px", borderBottom: "1px solid #f2f2f2" },
  rowLabel: { flex: "0 0 130px", fontSize: "12px", fontWeight: 600, color: "#333", display: "flex", alignItems: "center" },
  changedDot: { display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: "#ffb300", marginLeft: "6px" },
  opts: { flex: 1, display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
  opt: { display: "flex", alignItems: "center", padding: "3px 8px", border: "1px solid #ddd", borderRadius: "14px", fontSize: "12px", cursor: "pointer" },
  tag: { marginLeft: "4px", fontSize: "9px", color: "#e65100", border: "1px solid #ffcc80", borderRadius: "6px", padding: "0 3px" },
  reason: { flexBasis: "100%", fontSize: "10px", color: "#888", marginTop: "2px" },
  domBox: { marginTop: "16px", padding: "12px", background: "#f7f9fa", borderRadius: "8px", border: "1px solid #eceff1" },
  footer: { display: "flex", alignItems: "center", gap: "12px", padding: "12px 18px", borderTop: "1px solid #eee" },
  cancelBtn: { padding: "7px 14px", border: "1px solid #ccc", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" },
  applyBtn: { padding: "7px 16px", border: "none", borderRadius: "6px", color: "#fff", fontSize: "13px", fontWeight: 600 },
};
